import { Flex, Form, Spin } from 'antd';
import { CreateAccountArgs } from 'features/auth/auth-action-create-account';
import { useUpdatePassword } from 'features/auth/auth-action-set-user-password';
import { AuthSliceActions } from 'features/auth/authSlice';
import { useEffect } from 'react';
import { ConcussionRXLogo } from './concussionRx-logo';
import { ErrorMessage } from './ErrorMessage';
import { styles } from './styles';

import { LoadingOutlined } from '@ant-design/icons';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { BaseHeader } from 'components/header';
import { getClinicBasicInfo } from 'lib/api/firebase/apis/apis';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { useUpdateProfile } from 'lib/hooks/tanstack/use-update-profile';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useDispatch } from 'react-redux';
import { getUser } from '../../../lib/hooks/use-get-user';
import { CoreSignupForm, SignupFormProps } from './_components/form-signup';
import { useFormState } from './_utils/useFormState';
import { useHistory } from 'react-router-dom';

export interface CreateFormTypes
  extends Required<
    Pick<CreateAccountArgs, 'email' | 'password' | 'clinicName' | 'name'>
  > {
  confirm: string;
}

/**
 * The new user registration form
 */
export function InitAccountForm() {
  const { formState, setFormState } = useFormState();

  const authState = useAuth();
  const isUserDataLoading = authState.isUserDataLoading;
  const isAuthActionActive = authState.isAuthActionActive;
  const authSliceError = authState.error;

  const dispatch = useDispatch();
  const updatePasswordMutation = useUpdatePassword();
  const history = useHistory();

  useEffect(() => {
    async function setup() {
      if (authState.user === null) {
        return;
      }

      const { displayName, email } = authState.user;
      if (!displayName) {
        throw new Error(
          `User (uid=${authState.user.uid}) does not have a display name`
        );
      }

      if (!email) {
        throw new Error(
          `User (uid=${authState.user.uid}) does not have an email`
        );
      }

      const userData = await getUser(authState.user);

      const clinics = userData.clinics;
      if (!clinics) {
        throw new Error(
          `User (id=${authState.user.uid}) is not associated with an organisation. Please report this error to your organisation`
        );
      }

      const clinicInfo = await getClinicBasicInfo({ clinicId: clinics[0] });

      setFormState(_ => ({
        ..._,
        collectedData: {
          clinicName: clinicInfo.data.name,
          displayName: displayName,
          email: email
        },
        isLoading: false
      }));
    }

    setup().catch(error => {
      setFormState(s => ({
        ...s,
        isLoading: false,
        extraError: resolveError(error)
      }));
    });
  }, [authState.user, setFormState]);

  /**
   * Invoked when the form is submitted
   * @param values
   * @returns
   */
  async function onFinish(values: SignupFormProps) {
    if (!formState.collectedData) {
      setFormState(state => ({
        ...state,
        isLoading: false,
        extraError: new Error(
          'Form was submitted before the invitation details were loaded'
        )
      }));
      return;
    }

    const { password, email, name } = values;
    dispatch(AuthSliceActions.startLoadingAction());

    if (authState.user?.isPartial) {
      await updateProfileMutation.mutateAsync(
        { email, displayName: name },
        {
          onError() {
            dispatch(AuthSliceActions.stopLoadingAction());
          }
        }
      );
    }

    await updatePasswordMutation.mutateAsync(
      { password },
      {
        onError() {
          dispatch(AuthSliceActions.stopLoadingAction());
        }
      }
    );
    dispatch(AuthSliceActions.stopLoadingAction());

    history.push('/');
  }

  const updateProfileMutation = useUpdateProfile();

  const loading =
    formState.isLoading || isUserDataLoading || isAuthActionActive;
  if (loading) {
    return (
      <Flex justify={'center'} align="center">
        <div style={styles.container}>
          <Form key={'loading'}>
            <Flex justify="center">
              <ConcussionRXLogo />
            </Flex>
            <Flex
              justify={'center'}
              align={'center'}
              style={{ height: '100vh' }}
            >
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: '3rem' }}
                    spin={loading}
                  />
                }
              />
            </Flex>
          </Form>
        </div>
      </Flex>
    );
  }

  if (formState.extraError || formState.collectedData === null) {
    return (
      <Flex justify={'center'} align={'center'} flex={1}>
        <div style={styles.container}>
          <Form
            key="error display"
            className="login-form"
            style={styles.form}
            labelWrap={true}
          >
            <Flex style={styles.titleContainer} justify="center">
              <ConcussionRXLogo />
            </Flex>

            {formState.extraError ? (
              <ErrorDisplay
                title="Invitation error"
                description="Please contact your organisation to request a new invitation"
                error={formState.extraError}
              />
            ) : (
              <ErrorDisplay
                title="Invalid state"
                description="No invitation error was detected but the invitation details are not available"
                error={new Error()}
              />
            )}
          </Form>
        </div>
      </Flex>
    );
  }

  return (
    <Flex justify={'center'} align={'center'} flex={1}>
      <div style={styles.container}>
        <CoreSignupForm
          onFinish={onFinish}
          actionButtonText="Get Started"
          initialValues={{
            remember: true,
            email: authState.user?.isPartial
              ? ''
              : formState.collectedData?.email,
            name: authState.user?.isPartial
              ? ''
              : formState.collectedData?.displayName,
            clinicName: formState.collectedData.clinicName
          }}
          disabled={{
            email: authState.user?.isPartial ? false : true,
            name: authState.user?.isPartial ? false : true
          }}
        />
        {authSliceError && (
          <ErrorMessage errorMessage={authSliceError.message} />
        )}
        {updateProfileMutation.isError && (
          <ErrorMessage
            errorMessage={resolveError(updateProfileMutation.error).message}
          />
        )}
      </div>
    </Flex>
  );
}

/**
 * Wrapper for the create account form
 */
export function CreateAccount() {
  return (
    <>
      <BaseHeader headerStyle={{ background: 'none' }} />
      <InitAccountForm />
    </>
  );
}
